import { useNavigate } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useUserContext } from '../contexts/UserContext'
import User from '../../models/User'
import TablePagination from '../table/TablePagination'
import ContextHeader from '../common/ContextHeader'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import LoadingSpinner from '../common/pure/LoadingSpinner'
import { ArchiveButton, RestoreButton, TableActionContainer } from '../table/TableButton'
import Heading from '../common/pure/Heading'
import { Row } from '../common/pure/FlexContainer'
import { pi } from '../common/pure/PrimeIcon'
import t from '../../tools/t'

export default function UserManagementScreen() {
    const navigate = useNavigate()
    const { can, me, archiveUser, restoreUser, usersLoading, users } = useUserContext()
    const canWriteUsers = can(PermissionSuffix.UserWrite)

    const navigateToCreateUser = () => navigate('/users/create')

    const navigateToUserProfile = (user: User) => navigate('/users/' + user.id)

    const onArchiveUser = async (user: User) => {
        if (!confirm(`Are you sure you want to archive "${user.name}" from the system?`)) return
        await archiveUser(user.id)
    }

    const onRestoreUser = async (user: User) => await restoreUser(user.id)

    const loading = !me || usersLoading

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <Row arrangement="spaced" alignment="start">
                        <ContextHeader>
                            <Heading standalone size={2}>
                                {t('manage_users')}
                            </Heading>
                        </ContextHeader>
                        {canWriteUsers && (
                            <Button
                                label={t('user_create')}
                                icon={pi('plus')}
                                onClick={navigateToCreateUser}
                            />
                        )}
                    </Row>
                    <Card>
                        {loading ? (
                            <LoadingSpinner />
                        ) : (
                            <DataTable
                                dataKey="id"
                                value={users.items}
                                onRowClick={(event) => navigateToUserProfile(event.data as User)}
                                rowClassName={(user: User) =>
                                    user.isArchived ? 'archived-table-row' : undefined
                                }
                                rowHover={true}
                            >
                                <Column header={t('user_field_name')} field="name" />
                                <Column header={t('user_field_email')} field="email" />
                                <Column
                                    align="right"
                                    body={(data: User) => (
                                        <TableActionContainer>
                                            {canWriteUsers &&
                                                (data.isArchived ? (
                                                    <RestoreButton
                                                        target={data.id}
                                                        onClick={() => onRestoreUser(data)}
                                                    />
                                                ) : (
                                                    <ArchiveButton
                                                        target={data.id}
                                                        onClick={() => onArchiveUser(data)}
                                                    />
                                                ))}
                                        </TableActionContainer>
                                    )}
                                />
                            </DataTable>
                        )}
                        <TablePagination data={users} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
